/* @jsx h */
import { defineComponent, h, ref, onMounted, computed, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";

import { CitySelector } from "./components/CitySelector";
import { HeroSection } from "./components/HeroSection";
import { SearchBar } from "./components/SearchBar";
import { RestaurantFilters } from "./components/RestaurantFilters";
import { RestaurantCard } from "./components/RestaurantCard";
import { EventCard } from "./components/EventCard";
import { PublicHeaderComponent } from "../../components/PublicHeaderComponent";
import { PublicFooterComponent } from "../../components/PublicFooterComponent";
import { SeoHead } from "../../components/SeoHead";
import { fuzzyMatch } from "../../utils/fuzzyMatch";
import { getNextEventOccurrence } from "../../utils/eventSchedule";
import { useBranding } from "../../providers/Branding";
import {
  fetchEatsGuideRestaurants,
  fetchEvents,
  type EatsGuideCity,
} from "../../../api/client";
import { EventWithShop } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { CustomerLanguageSelector } from "../../components/CustomerLanguageSelector";
import { CollectedBottomNav } from "../CollectedPage/CollectedBottomNav";

interface Restaurant {
  identifier: string;
  slug: string | null;
  name: { en: string | null };
  textTeaser: { en: string | null };
  image: { url: string };
  address: {
    streetAddress: string;
    addressLocality: string;
  };
  category: Record<string, any>;
  openingHours: string | string[];
  features?: string[];
  googleRating?: number | null;
  googleReviewCount?: number | null;
}

export const HomePageInner = defineComponent({
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { branding, defaultCity, cities: brandingCities } = useBranding();
    const { t } = useLocalization();
    const restaurants = ref<Restaurant[]>([]);
    const events = ref<EventWithShop[]>([]);
    const loading = ref(true);
    const selectedCity = ref(defaultCity.value || "all-swiss");
    const showAllRestaurants = ref(false);
    const showAllEvents = ref(false);
    const searchQuery = ref("");
    const selectedFeatures = ref<string[]>([]);
    const minGoogleRating = ref(0);
    const currentCityInfo = ref<EatsGuideCity | null>(null);

    const loadRestaurants = async (city: string) => {
      try {
        const response = await fetchEatsGuideRestaurants(city);
        currentCityInfo.value = response.city;

        // Filter out restaurants with missing essential data
        // For Zurich (JSON data), apply strict filtering for placeholder images
        const isZurich = city.toLowerCase() === "zurich";
        restaurants.value = (response.data as Restaurant[]).filter(
          (restaurant: any) => {
            const hasName = restaurant.name?.en;
            const hasAddress =
              restaurant.address?.streetAddress ||
              restaurant.address?.addressLocality;

            if (!hasName || !hasAddress) return false;

            // For Zurich, filter out placeholder images
            if (isZurich) {
              const hasValidImage =
                restaurant.image?.url &&
                !restaurant.image.url.includes(
                  "img_placeholder_test_mit_rahmen_grauer_hintergrund_1280x960.png",
                );
              const hasTeaser = restaurant.textTeaser?.en;
              if (!hasValidImage || !hasTeaser) return false;
            }

            return true;
          },
        );
      } catch (error) {
        console.error("Failed to load restaurant data:", error);
        restaurants.value = [];
      }
    };

    const loadEvents = async () => {
      try {
        // TODO: Replace 'demo' with actual shop slug when available
        const allEvents = await fetchEvents(selectedCity.value);

        // Get current date/time
        const now = new Date();

        // Keep events that still have an upcoming occurrence, then sort by that
        // occurrence. For recurring events this looks at the next future date
        // from the rrule rather than the (usually past) first occurrence, so
        // ongoing recurring events stay visible.
        events.value = allEvents
          .map((event) => ({
            event,
            nextOccurrence: getNextEventOccurrence(event, now),
          }))
          .filter(({ nextOccurrence }) => nextOccurrence !== null)
          .sort(
            (a, b) => a.nextOccurrence!.getTime() - b.nextOccurrence!.getTime(),
          )
          .map(({ event }) => event);
      } catch (error) {
        console.error("Failed to load events:", error);
        events.value = [];
      }
    };

    const loadData = async (city: string) => {
      try {
        loading.value = true;
        await Promise.all([loadRestaurants(city), loadEvents()]);
      } finally {
        loading.value = false;
      }
    };

    const handleCityChange = async (city: string) => {
      selectedCity.value = city;
      router.push({ query: { ...route.query, city } });
      await loadData(city);
    };

    // Sync filter state to/from URL
    const syncFiltersToUrl = () => {
      const query: Record<string, string> = { city: selectedCity.value };
      if (selectedFeatures.value.length > 0) {
        query.features = selectedFeatures.value.join(",");
      }
      if (minGoogleRating.value > 0) {
        query.rating = String(minGoogleRating.value);
      }
      router.replace({ query });
    };

    watch(selectedFeatures, syncFiltersToUrl, { deep: true });
    watch(minGoogleRating, syncFiltersToUrl);

    onMounted(async () => {
      const urlCity = route.query.city as string;
      if (urlCity && brandingCities.value.some((c) => c.slug === urlCity)) {
        selectedCity.value = urlCity;
      } else {
        selectedCity.value = defaultCity.value || "all-swiss";
      }

      // Restore filters from URL
      const urlFeatures = route.query.features as string;
      if (urlFeatures) {
        selectedFeatures.value = urlFeatures.split(",").filter(Boolean);
      }
      const urlRating = route.query.rating as string;
      if (urlRating) {
        minGoogleRating.value = parseFloat(urlRating) || 0;
      }

      router.replace({ query: { ...route.query, city: selectedCity.value } });
      await loadData(selectedCity.value);
    });

    watch(
      () => route.query.city,
      async (newCity) => {
        if (
          newCity &&
          typeof newCity === "string" &&
          newCity !== selectedCity.value
        ) {
          selectedCity.value = newCity;
          await loadData(newCity);
        }
      },
    );

    const hasGoogleRatings = computed(() => {
      return restaurants.value.some(
        (r) => r.googleRating && r.googleRating > 0,
      );
    });

    const filteredRestaurants = computed(() => {
      let filtered = restaurants.value;

      if (searchQuery.value) {
        const q = searchQuery.value;
        filtered = filtered.filter(
          (restaurant) =>
            fuzzyMatch(restaurant.name.en, q) ||
            fuzzyMatch(restaurant.textTeaser?.en, q) ||
            Object.keys(restaurant.category || {}).some((cat) =>
              fuzzyMatch(cat, q),
            ),
        );
      }

      if (selectedFeatures.value.length > 0) {
        filtered = filtered.filter((restaurant) => {
          const features = restaurant.features || [];
          return selectedFeatures.value.every((f) => features.includes(f));
        });
      }

      if (minGoogleRating.value > 0) {
        filtered = filtered.filter(
          (restaurant) =>
            restaurant.googleRating &&
            restaurant.googleRating >= minGoogleRating.value,
        );
      }

      return filtered;
    });

    const hasActiveFilters = computed(() => {
      return (
        searchQuery.value ||
        selectedFeatures.value.length > 0 ||
        minGoogleRating.value > 0
      );
    });

    // Show 6 unless expanded or filters are active (always show all when filtering)
    const visibleRestaurants = computed(() => {
      if (showAllRestaurants.value || hasActiveFilters.value) {
        return filteredRestaurants.value;
      }
      return filteredRestaurants.value.slice(0, 6);
    });

    const hasMoreRestaurants = computed(() => {
      return (
        !showAllRestaurants.value &&
        !hasActiveFilters.value &&
        filteredRestaurants.value.length > 6
      );
    });

    const visibleEvents = computed(() => {
      return showAllEvents.value ? events.value : events.value.slice(0, 3);
    });

    const hasMoreEvents = computed(() => {
      return !showAllEvents.value && events.value.length > 3;
    });

    // Dynamic city name for display
    const currentCityName = computed(() => {
      return currentCityInfo.value?.name || selectedCity.value;
    });

    const structuredData = computed(() => ({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: `${branding.value?.name} ${branding.value?.subtitle}`,
      description: t("customer.home.seo_description", [currentCityName.value]),
      url: typeof window !== "undefined" ? window.location.origin : "",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${
            typeof window !== "undefined" ? window.location.origin : ""
          }/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    }));

    return () => (
      <div class="min-h-screen pb-24 sm:pb-28">
        <SeoHead
          title={t("customer.home.seo_title", [
            `${branding.value?.name} ${branding.value?.subtitle}`,
            currentCityName.value,
          ])}
          description={t("customer.home.seo_description_full", [
            currentCityName.value,
          ])}
          keywords={`restaurants ${currentCityName.value}, dining, food guide, restaurant reviews, culinary experiences`}
          structuredData={structuredData.value}
        />
        <PublicHeaderComponent>
          <CustomerLanguageSelector brandId={branding.value?.id} />
        </PublicHeaderComponent>

        <main id="main-content" class="max-w-7xl mx-auto px-4">
          {/* Hero Section with Search */}
          <HeroSection cityName={currentCityName.value}>
            {{
              cityControl: () =>
                brandingCities.value.length > 1 ? (
                  <CitySelector
                    cities={brandingCities.value}
                    selectedCity={selectedCity.value}
                    onCityChange={handleCityChange}
                  />
                ) : null,
              default: () => (
                <SearchBar
                  placeholder={t("customer.home.search_restaurants")}
                  restaurants={restaurants.value}
                  onSearch={(query: string) => {
                    searchQuery.value = query;
                  }}
                />
              ),
            }}
          </HeroSection>

          {/* Filters */}
          <div class="pt-4 pb-4">
            <RestaurantFilters
              selectedFeatures={selectedFeatures.value}
              minGoogleRating={minGoogleRating.value}
              hasGoogleRatings={hasGoogleRatings.value}
              onUpdate:selectedFeatures={(val: string[]) =>
                (selectedFeatures.value = val)
              }
              onUpdate:minGoogleRating={(val: number) =>
                (minGoogleRating.value = val)
              }
            />
          </div>

          {/* Restaurants Section */}
          <div style="padding-bottom: clamp(48px, 6vw, 96px);">
            <p
              class="text-gray-500 font-light text-sm mb-6"
              role="status"
              aria-live="polite"
            >
              {loading.value
                ? t("customer.home.loading")
                : t(
                    filteredRestaurants.value.length === 1
                      ? "customer.home.restaurant_count_one"
                      : "customer.home.restaurant_count_many",
                    [filteredRestaurants.value.length, currentCityName.value],
                  )}
            </p>

            {loading.value ? (
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-8">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    class="rounded-2xl border border-gray-100 bg-white overflow-hidden"
                  >
                    <div class="aspect-[4/3] skeleton-shimmer rounded-t-2xl" />
                    <div class="px-4 md:px-5 pb-5 pt-4 space-y-3">
                      <div class="h-6 w-3/4 rounded skeleton-shimmer" />
                      <div class="h-4 w-1/2 rounded skeleton-shimmer" />
                      <div class="pt-3 border-t border-gray-100">
                        <div class="h-4 w-24 rounded skeleton-shimmer mx-auto" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : visibleRestaurants.value.length > 0 ? (
              <>
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-8"
                  data-reveal-stagger
                >
                  {visibleRestaurants.value.map((restaurant) => (
                    <RestaurantCard
                      key={restaurant.identifier}
                      restaurant={restaurant}
                      city={selectedCity.value}
                      newTab
                    />
                  ))}
                </div>
                {/* View All — expands in place */}
                {hasMoreRestaurants.value && (
                  <div class="text-center">
                    <button
                      type="button"
                      onClick={() => (showAllRestaurants.value = true)}
                      class="btn-accent inline-flex items-center gap-2 px-6 py-3 rounded-lg font-light cursor-pointer"
                    >
                      {t("customer.home.view_all_restaurants", [
                        filteredRestaurants.value.length,
                      ])}
                      <span class="material-symbols-outlined text-lg">
                        expand_more
                      </span>
                    </button>
                  </div>
                )}
                {showAllRestaurants.value && !hasActiveFilters.value && (
                  <div class="text-center">
                    <button
                      type="button"
                      onClick={() => (showAllRestaurants.value = false)}
                      class="inline-flex items-center gap-2 px-4 py-2 font-light text-sm cursor-pointer hover:opacity-80"
                      style="color: var(--accent); transition: opacity 200ms ease;"
                    >
                      {t("customer.home.show_less")}
                      <span class="material-symbols-outlined text-sm">
                        expand_less
                      </span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div class="text-center py-16">
                <div class="text-6xl mb-6">🔍</div>
                <h3 class="text-2xl font-serif font-light text-gray-900 mb-4">
                  {t("customer.home.no_restaurants_found")}
                </h3>
                <p class="text-gray-600 font-light max-w-md mx-auto">
                  {hasActiveFilters.value
                    ? t("customer.home.adjust_search_or_filters")
                    : t("customer.home.no_restaurants_in_city", [
                        currentCityName.value,
                      ])}
                </p>
              </div>
            )}
          </div>

          {/* Events Section */}
          <div style="padding-bottom: clamp(48px, 6vw, 96px);">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl section-heading text-gray-900">
                {t("customer.home.events")}
              </h2>
              {events.value.length > 0 && (
                <p class="text-gray-500 font-light text-sm">
                  {t("customer.home.upcoming_count", [events.value.length])}
                </p>
              )}
            </div>

            {loading.value ? (
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-8">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    class="rounded-2xl border border-gray-100 bg-white overflow-hidden"
                  >
                    <div class="aspect-[4/3] skeleton-shimmer rounded-t-2xl" />
                    <div class="px-4 md:px-5 pb-5 pt-4 space-y-3">
                      <div class="h-6 w-3/4 rounded skeleton-shimmer" />
                      <div class="h-4 w-1/2 rounded skeleton-shimmer" />
                    </div>
                  </div>
                ))}
              </div>
            ) : events.value.length > 0 ? (
              <>
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-8"
                  data-reveal-stagger
                >
                  {visibleEvents.value.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      city={selectedCity.value}
                      compact={true}
                    />
                  ))}
                </div>

                {hasMoreEvents.value && (
                  <div class="text-center">
                    <button
                      type="button"
                      onClick={() => (showAllEvents.value = true)}
                      class="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg font-light hover:bg-gray-800 transition-colors"
                    >
                      {t("customer.home.view_all_events", [
                        events.value.length,
                      ])}
                      <span class="material-symbols-outlined text-lg">
                        expand_more
                      </span>
                    </button>
                  </div>
                )}
                {showAllEvents.value && (
                  <div class="text-center">
                    <button
                      type="button"
                      onClick={() => (showAllEvents.value = false)}
                      class="inline-flex items-center gap-2 px-4 py-2 text-gray-500 font-light hover:text-gray-700 transition-colors text-sm"
                    >
                      {t("customer.home.show_less")}
                      <span class="material-symbols-outlined text-sm">
                        expand_less
                      </span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div class="text-center py-12">
                <div class="text-gray-400 font-light">
                  {t("customer.home.no_upcoming_events")}
                </div>
              </div>
            )}
          </div>
        </main>

        <PublicFooterComponent />

        <CollectedBottomNav />
      </div>
    );
  },
});

export const HomePage = defineComponent({
  setup() {
    return () => <HomePageInner />;
  },
});
